import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { MapPin, Phone, Mail, Building2 } from 'lucide-react';

import { getEmployee, getEmployeePersonnel, getPersonalInformation } from '../../../services/employeeService';
import { shouldShowExitInfo, exitReasonLabel } from '../exitInfo';
import { formatDate } from '../../../utils/Utils';
import { useAuth } from '../../../context/AuthContext';
import Avatar from '../../../components/ui/Avatar';
import PersonCell from '../../../components/ui/PersonCell';
import Tooltip from '../../../components/ui/Tooltip';
import { formatPhone } from '../../../utils/phone';
import { moduleName } from '../../../utils/moduleNames';
import { priorServiceParts } from '../priorService';

const getWorkDuration = (employmentDate, t) => {
  if (!employmentDate) return '-';
  const now = new Date();
  const start = new Date(employmentDate);
  // Gelecek tarihli işe giriş (ör. hatalı veri girişi) → henüz çalışmaya
  // başlamamış. Eskiden negatif ay farkı (ör. -3) `months += 12` ile 12'ye
  // sarmalanıp "9 ay" gibi yanlış bir değer üretiyordu; çalışma süresi negatif
  // olamaz, 0 göster.
  if (start > now) return `0 ${t('employee.months')}`;
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  // İçinde bulunulan ayın gün eşiği henüz dolmadıysa o ayı tam sayma
  // (ör. 20'sinde başlamış, bugün ayın 17'si → o ay henüz tamamlanmadı).
  if (now.getDate() < start.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const parts = [];
  if (years > 0) parts.push(`${years} ${t('employee.years')}`);
  if (months > 0) parts.push(`${months} ${t('employee.months')}`);
  return parts.length > 0 ? parts.join(' ') : `0 ${t('employee.months')}`;
};

const censorCitizenId = (citizenId) => {
  if (!citizenId || citizenId.length < 5) return citizenId || '-';
  return citizenId.slice(0, 3) + '******' + citizenId.slice(-2);
};

// KVKK — hassas alanlar (TCKN, doğum tarihi, adres, telefon, kişisel iletişim, sicil no,
// yaka tipi, sözleşme/deneme/çıkış bilgisi) yalnız İK'ya (EM.Write) ve kişinin KENDİSİNE
// gösterilir; backend de diğerlerine bu alanları hiç göndermez (EmployeeDetailAccess).
// "Kendisi" kimlik ailesiyle: oturumun çalışan kimliği ya da (eski kayıtlarda) kullanıcı kimliği.
export const isSelfEmployee = (user, employeeId) => {
  if (!user || !employeeId) return false;
  const target = String(employeeId).toLowerCase();
  return [user.employeeId, user.id, user.userId]
    .filter(Boolean)
    .some((key) => String(key).toLowerCase() === target);
};

export default function EmployeeGeneralTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const { canWrite, user } = useAuth();
  const canSeeSensitive = canWrite('EM') || isSelfEmployee(user, employeeId);
  const history = useHistory();
  const lang = i18n.language;

  const [employee, setEmployee] = useState(null);
  const [personnel, setPersonnel] = useState(null);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getEmployee(employeeId).then((res) => res.data?.data).catch(() => null),
      getEmployeePersonnel(employeeId).then((res) => res.data?.data).catch(() => null),
      getPersonalInformation(employeeId).then((res) => res.data?.data).catch(() => null),
    ]).then(([emp, per, pi]) => {
      setEmployee(emp);
      setPersonnel(per);
      setPersonalInfo(pi);
    }).finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <LoadingText>{t('common.loading') || '...'}</LoadingText>;
  if (!employee) return null;

  const collarLabel = employee.collarType === 1
    ? t('employee.whiteCollar')
    : employee.collarType === 2
      ? t('employee.blueCollar')
      : '-';

  const priorParts = priorServiceParts(employee.priorServiceDays);
  const priorServiceText = employee.priorServiceDays == null
    ? '-'
    : `${employee.priorServiceDays}${priorParts ? ` (${t('employee.priorServiceHint', priorParts)})` : ''}`;

  const manager = personnel?.manager;
  const subordinates = personnel?.subordinates || [];

  const handlePersonClick = (userId) => {
    history.push(`/employees/${userId}`);
  };

  return (
    <Container>
      <LeftSection>
        <ProfileHeader>
          <Avatar
            src={employee.profilePicture}
            firstName={employee.firstName || ''}
            lastName={employee.lastName || ''}
            size={80}
            variant="circle"
          />
          <ProfileInfo>
            <EmployeeName>{employee.firstName} {employee.lastName}</EmployeeName>
            <EmployeeTitle>{employee.titleName || '-'}</EmployeeTitle>
            <EmployeeDepartment>{employee.departmentName || '-'}</EmployeeDepartment>
          </ProfileInfo>
        </ProfileHeader>

        <InfoGrid>
          <InfoItem>
            <InfoLabel>{t('employee.employmentDate')}</InfoLabel>
            <InfoValue>{formatDate(employee.employmentDate, lang)}</InfoValue>
          </InfoItem>
          {canSeeSensitive && (
            <>
              <InfoItem>
                <InfoLabel>{t('employee.registrationNumber')}</InfoLabel>
                <InfoValue>{employee.registrationNumber || '-'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.citizenId')}</InfoLabel>
                <InfoValue>
                  {canWrite('EM') ? (employee.citizenId || '-') : censorCitizenId(employee.citizenId)}
                </InfoValue>
              </InfoItem>
            </>
          )}
          <InfoItem>
            <InfoLabel>{t('employee.workDuration')}</InfoLabel>
            <InfoValue>{getWorkDuration(employee.employmentDate, t)}</InfoValue>
          </InfoItem>
          {/* Kademe / iş ailesi YALNIZ İK'ya (EM.Write) — kişinin kendisi de görmez (spec 2026-10-27 §6.4);
              bu yüzden canSeeSensitive DEĞİL canWrite('EM'). */}
          {canWrite('EM') && (
            <>
              <InfoItem data-testid="info-grade">
                <InfoLabel>{t('jobGrade.grade')}</InfoLabel>
                <InfoValue>{employee.gradeName || '-'}</InfoValue>
              </InfoItem>
              <InfoItem data-testid="info-job-family">
                <InfoLabel>{t('jobFamily.jobFamily')}</InfoLabel>
                <InfoValue>{employee.jobFamilyName || '-'}</InfoValue>
              </InfoItem>
            </>
          )}
          {/* İşten çıkış bilgisi (müşteri geri bildirimi 2026-08): pasif çalışanda
              her zaman görünür — elle deaktive edilenlerde veri yoksa "-" (dürüst
              boşluk). OD modüllü tenant'ta form bu alanları gizlediği için burası
              çıkış bilgisinin TEK salt-okunur yüzeyidir. */}
          {canSeeSensitive && shouldShowExitInfo(employee) && (
            <>
              <InfoItem>
                <InfoLabel>{t('employee.terminationDate')}</InfoLabel>
                <InfoValue>{employee.terminationDate ? formatDate(employee.terminationDate, lang) : '-'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.exitReasonCode')}</InfoLabel>
                <InfoValue>{exitReasonLabel(t, employee.exitReasonCode)}</InfoValue>
              </InfoItem>
            </>
          )}
          {canSeeSensitive && (
            <>
              <InfoItem>
                <InfoLabel>{t('employee.contractEndDate')}</InfoLabel>
                <InfoValue>{formatDate(employee.contractEndDate, lang)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.probationEndDate')}</InfoLabel>
                <InfoValue>{formatDate(employee.probationEndDate, lang)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.terminationDate')}</InfoLabel>
                <InfoValue>{formatDate(employee.terminationDate, lang)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.exitReasonCode')}</InfoLabel>
                <InfoValue>
                  {employee.exitReasonCode ? t(`employee.exitReasons.${employee.exitReasonCode}`, '-') : '-'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.collarType')}</InfoLabel>
                <InfoValue>{collarLabel}</InfoValue>
              </InfoItem>
              {/* AKM POC §6.1/§6.4 — kamu projeksiyonu bu iki alanı taşımaz (EmployeeDetailAccess.ToPublic). */}
              <InfoItem>
                <InfoLabel>{t('employee.employeeStatus')}</InfoLabel>
                <InfoValue>{employee.employeeStatusName || '-'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.priorServiceDays')}</InfoLabel>
                <InfoValue>{priorServiceText}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.dateOfBirth')}</InfoLabel>
                <InfoValue>{formatDate(employee.dateOfBirth, lang)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>{t('employee.address')}</InfoLabel>
                <InfoValue>{employee.address || '-'}</InfoValue>
              </InfoItem>
            </>
          )}
          <InfoItem>
            <InfoLabel>{t('employee.branch')}</InfoLabel>
            <InfoValue>
              {employee.locationName ? (
                <LocationRow>
                  <MapPin size={14} />
                  {employee.locationName}
                </LocationRow>
              ) : '-'}
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('employee.manager')}</InfoLabel>
            <InfoValue>
              {manager ? (
                <ManagerLink onClick={() => handlePersonClick(manager.id)}>
                  {manager.firstName} {manager.lastName}
                  {manager.titleName ? <ManagerLinkTitle> · {manager.titleName}</ManagerLinkTitle> : null}
                </ManagerLink>
              ) : '-'}
            </InfoValue>
          </InfoItem>
        </InfoGrid>

        <ContactCard>
          <CardTitle>{t('common.contact') || 'Contact'}</CardTitle>
          <ContactGrid>
            {canSeeSensitive && (
              <ContactItem>
                <Tooltip text={t('employee.workPhone')}>
                  <ContactIcon><Phone size={14} /></ContactIcon>
                </Tooltip>
                <ContactText>
                  <ContactLabel>{t('employee.workPhone')}</ContactLabel>
                  <ContactValue>
                    {formatPhone(employee.areaCode, employee.phoneNumber)}
                  </ContactValue>
                </ContactText>
              </ContactItem>
            )}
            <ContactItem>
              <Tooltip text={t('employee.workEmail')}>
                <ContactIcon><Mail size={14} /></ContactIcon>
              </Tooltip>
              <ContactText>
                <ContactLabel>{t('employee.workEmail')}</ContactLabel>
                <ContactValue>{employee.corporateEmail || '-'}</ContactValue>
              </ContactText>
            </ContactItem>
            {canSeeSensitive && (
              <>
                <ContactItem>
                  <Tooltip text={t('employee.personalPhone')}>
                    <ContactIcon $personal><Phone size={14} /></ContactIcon>
                  </Tooltip>
                  <ContactText>
                    <ContactLabel>{t('employee.personalPhone')}</ContactLabel>
                    <ContactValue>{employee.personalPhone || '-'}</ContactValue>
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <Tooltip text={t('employee.personalEmail')}>
                    <ContactIcon $personal><Mail size={14} /></ContactIcon>
                  </Tooltip>
                  <ContactText>
                    <ContactLabel>{t('employee.personalEmail')}</ContactLabel>
                    <ContactValue>{employee.personalEmail || '-'}</ContactValue>
                  </ContactText>
                </ContactItem>
              </>
            )}
          </ContactGrid>
        </ContactCard>

        {personalInfo && (
          (personalInfo.educationLevel || personalInfo.educationStatus
            || personalInfo.lastInstitution || personalInfo.faculty) ? (
            <ContactCard>
              <CardTitle>{t('employee.education')}</CardTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>{t('employee.educationLevel')}</InfoLabel>
                  <InfoValue>
                    {personalInfo.educationLevel
                      ? t(`employee.eduLevel.${personalInfo.educationLevel}`, '-')
                      : '-'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>{t('employee.educationStatus')}</InfoLabel>
                  <InfoValue>
                    {personalInfo.educationStatus
                      ? t(`employee.eduStatus.${personalInfo.educationStatus}`, '-')
                      : '-'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>{t('employee.lastInstitution')}</InfoLabel>
                  <InfoValue>{personalInfo.lastInstitution || '-'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>{t('employee.faculty')}</InfoLabel>
                  <InfoValue>{personalInfo.faculty || '-'}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </ContactCard>
          ) : null
        )}
      </LeftSection>

      <RightSection>
        {manager && (
          <SideCard $accent>
            <CardTitle>{t('employee.manager')}</CardTitle>
            <ManagerContent>
              <Avatar
                src={manager.profilePicture}
                firstName={manager.firstName || ''}
                lastName={manager.lastName || ''}
                size={48}
                variant="circle"
              />
              <ManagerInfo>
                <ManagerName
                  onClick={() => handlePersonClick(manager.id)}
                >
                  {manager.firstName} {manager.lastName}
                </ManagerName>
                <ManagerTitle>{manager.titleName || '-'}</ManagerTitle>
              </ManagerInfo>
            </ManagerContent>
          </SideCard>
        )}

        <SideCard>
          <CardTitle>{t('employee.subordinates')}</CardTitle>
          {subordinates.length === 0 ? (
            <EmptyText>{t('employee.noSubordinates')}</EmptyText>
          ) : (
            <SubordinatesList>
              {subordinates.map((sub) => (
                <SubordinateRow key={sub.id}>
                  <PersonCell
                    name={`${sub.firstName} ${sub.lastName}`}
                    profilePicture={sub.profilePicture}
                    onClick={() => handlePersonClick(sub.id)}
                  />
                </SubordinateRow>
              ))}
            </SubordinatesList>
          )}
        </SideCard>

        {Array.isArray(employee.seats) && employee.seats.length > 0 && (
          <SideCard>
            <CardTitle>{t('license.title')}</CardTitle>
            <SeatTagList>
              {/* Kü-7 — HAM MODÜL KODU ("SM", "PM", "AT") DEĞİL modül adı gösterilir.
                  Eşleme sol menünün grup etiketlerinden gelir (utils/moduleNames.js);
                  bilinmeyen kodda kodun kendisi basılır, bilgi kaybolmaz. */}
              {employee.seats.map((code) => (
                <SeatTag key={code}>{moduleName(t, code)}</SeatTag>
              ))}
            </SeatTagList>
          </SideCard>
        )}
      </RightSection>
    </Container>
  );
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoadingText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  padding: 40px;
  text-align: center;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #E8ECF0;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmployeeName = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const EmployeeTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const EmployeeDepartment = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #E8ECF0;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const LocationRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ManagerLink = styled.span`
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;
const ManagerLinkTitle = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: normal;
`;

const ContactCard = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #E8ECF0;
`;

const CardTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const ContactIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $personal }) => $personal ? '#fef3c7' : '#dbeafe'};
  color: ${({ $personal }) => $personal ? '#b45309' : '#2563eb'};
  flex-shrink: 0;
`;

const ContactText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContactLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
`;

const ContactValue = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const SideCard = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #E8ECF0;
  ${({ $accent }) => $accent && `border-left: 3px solid #EF4444;`}
`;

const ManagerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ManagerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ManagerName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ManagerTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
`;

const EmptyText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #A0A8B4;
`;

// Görev 18 — kişinin koltuğu (lisansı) olan ölçümlü modüllerin etiket listesi.
// `employee.seats` alanı yoksa (backend henüz eklemediyse ya da varsayılan
// modülden ibaret bir çalışansa) kart HİÇ gösterilmez — boş liste "hiçbir
// modülde koltuğu yok" demek değildir, "bilgi yok" demektir.
const SeatTagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SeatTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: rgba(2, 211, 191, 0.1);
  color: #02D3BF;
`;

const SubordinatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
`;

const SubordinateRow = styled.div`
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;
